import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * Les **quinze blocs**, dans l'ordre d'impression — `RP_BLOCKS` du prototype validé
 * (`ghp-bo/ext.js:130`), titres et sous-titres verbatim.
 *
 * ⚠️ **Énumération fermée, à rebours des vocabulaires du client** (`Inquiry.purpose`,
 * `Inspection.visit_kind`) : [M10](../../../../docs/MODELE-DONNEES.md#m10) trace la ligne — on
 * contraint ce dont notre code dépend, et `composeReport` branche sur chacun de ces noms. Un
 * seizième bloc est un changement de code, pas une donnée.
 *
 * ⚠️ **Les codes sont les nôtres, pas ceux du prototype.** Il abrège en `moyens`, `signCtrl`,
 * `anoms` ; le dépôt écrit ses identifiants en anglais, sans exception. La correspondance avec
 * les champs de `ReportContent` — et les **trois blocs qui n'en ont pas** — est en M33.
 */
export const REPORT_BLOCKS = [
    {
        code: 'methods',
        page: 1,
        title: 'Moyens mobilisés',
        subtitle: 'Photos · Caméra · Topo · Fumée',
        locked: false,
    },
    {
        code: 'general',
        page: 1,
        title: 'Informations générales',
        subtitle: 'Type de contrôle · client · visite · contrôleur',
        locked: true,
    },
    {
        code: 'owner',
        page: 1,
        title: 'Propriétaire de l’habitation',
        subtitle: 'Nom · téléphone · adresse · mail',
        locked: false,
    },
    {
        code: 'dwelling',
        page: 1,
        title: 'Caractéristiques de l’habitation',
        subtitle: 'Résidence · année · occupants · pièces',
        locked: false,
    },
    {
        code: 'plot',
        page: 2,
        title: 'Contexte du terrain',
        subtitle: 'Superficie · AEP · réseau public · puits',
        locked: false,
    },
    {
        code: 'connection_table',
        page: 2,
        title: 'Tableau de raccordement',
        subtitle: 'Points d’eau par niveau et réseau',
        locked: true,
    },
    {
        code: 'connection_equipment',
        page: 2,
        title: 'Clapets, relevage & servitudes',
        subtitle: 'Boîtes de raccordement EU / EP',
        locked: false,
    },
    {
        code: 'connected_networks',
        page: 3,
        title: 'Eaux pluviales',
        subtitle: 'Élément × exutoire · totaux EU et unitaire · débit du drain',
        locked: false,
    },
    {
        code: 'anomalies',
        page: 3,
        title: 'Anomalies constatées',
        subtitle: 'Liste des non-conformités relevées',
        locked: true,
    },
    {
        code: 'recommendations',
        page: 3,
        title: 'Travaux à réaliser',
        subtitle: 'Préconisations et délais',
        locked: false,
    },
    {
        code: 'conclusion',
        page: 4,
        title: 'Conclusion du contrôle',
        subtitle: 'Raccordé / partiellement / non raccordé',
        locked: true,
    },
    {
        code: 'schematic',
        page: 4,
        title: 'Schéma de l’installation',
        subtitle: 'Croquis terrain',
        locked: false,
    },
    {
        code: 'photos',
        page: 4,
        title: 'Annexe photographique',
        subtitle: 'Photos légendées du contrôle',
        locked: false,
    },
    {
        code: 'client_signature',
        page: 4,
        title: 'Signature du client',
        subtitle: 'Paraphe de la personne présente',
        locked: false,
    },
];
export const REPORT_BLOCK_CODES = [
    'methods',
    'general',
    'owner',
    'dwelling',
    'plot',
    'connection_table',
    'connection_equipment',
    'connected_networks',
    'anomalies',
    'recommendations',
    'conclusion',
    'schematic',
    'photos',
    'client_signature',
];
export const reportBlockSchema = z.enum(REPORT_BLOCK_CODES);
/**
 * Les **quatre blocs qu'aucune règle ne retire** — le prototype les marque d'un cadenas, et le
 * rail les rend non cliquables.
 *
 * Ils portent ce qui fait du document un rapport de contrôle : qui a contrôlé quoi et quand, le
 * relevé du raccordement, ce qui ne va pas, et le verdict. Un PDF qui porterait l'en-tête « Rapport
 * de contrôle de raccordement » sans sa conclusion serait un faux poli.
 */
export const LOCKED_REPORT_BLOCKS = REPORT_BLOCKS.filter((block) => block.locked).map((block) => block.code);
/** La description d'un bloc, par son code — titre et sous-titre du rail, page de l'aperçu. */
export function reportBlock(code) {
    // `find` ne peut pas échouer : le type du paramètre est l'énumération des codes présents.
    return REPORT_BLOCKS.find((block) => block.code === code);
}
/**
 * Une **règle du rapport** : une cible, les blocs qu'on lui retire, et pourquoi.
 *
 * ⚠️ **Deux clés étrangères plutôt qu'un couple `(scope, target)` en texte.** Le prototype range
 * ses règles par libellé (`target: 'Mauges Communauté'`) — c'est une maquette : une cible en texte
 * ne se contraint pas, et se désynchronise au premier renommage du client ou du secteur
 * ([M22](../../../../docs/MODELE-DONNEES.md#m22)). `Lot` et `Client` sont en base, la règle les
 * référence.
 *
 * ⚠️ **Aucune colonne `scope`** : elle se lit sur la cible (`reportRuleScope`). Une troisième
 * source de vérité pour la même information diverge au premier `UPDATE` partiel.
 *
 * ⚠️ **Une cible n'a qu'une règle**, et c'est la base qui le tient (`UNIQUE` sur chacune des deux
 * clés) : deux règles sur le même secteur se contrediraient sans que rien ne les départage.
 *
 * ⚠️ **Aucune règle est le cas courant.** Un rapport complet n'a pas de ligne — le dépôt rend
 * `null`, et `hidesReportBlock` l'accepte. Créer une règle vide à l'ouverture de l'écran
 * remplirait la table de lignes qui ne disent rien et ferait croire à une configuration.
 */
export const reportRuleSchema = z
    .object({
    id: uuidV7Schema,
    /** Le secteur (`Lot`) visé, ou `null` quand la règle vise un client (#661). */
    lotId: uuidV7Schema.nullable(),
    /** Le client visé, ou `null` quand la règle vise un secteur. */
    clientId: uuidV7Schema.nullable(),
    /**
     * Les blocs retirés du rapport livré. **Un ensemble, pas un ordre** : le rapport garde le
     * sien, la règle ne fait que soustraire.
     */
    hiddenBlocks: z.array(reportBlockSchema),
    /**
     * La note du gestionnaire — « Rapport allégé — Nantes Métropole », « Photos transmises
     * séparément ». Texte **libre** : c'est une phrase pour la personne qui lira la règle dans six
     * mois, affichée sous la liste des blocs retirés, jamais interprétée.
     */
    note: z.string().nullable(),
})
    .refine((rule) => (rule.lotId === null) !== (rule.clientId === null), {
    message: 'une règle vise un secteur ou un client, jamais les deux ni aucun',
    path: ['lotId'],
})
    .refine((rule) => !rule.hiddenBlocks.some((block) => LOCKED_REPORT_BLOCKS.includes(block)), {
    message: 'les blocs obligatoires du rapport ne se retirent pas',
    path: ['hiddenBlocks'],
});
/**
 * La portée d'une règle, **lue sur sa cible**. C'est la bascule du haut du rail, et elle se
 * déduit : une règle qui porte un `lot_id` est une règle de secteur, un point c'est tout.
 */
export function reportRuleScope(rule) {
    return rule.lotId === null ? 'client' : 'lot';
}
/**
 * Ce bloc disparaît-il du rapport livré ?
 *
 * ⚠️ **Un bloc obligatoire ne disparaît jamais, quoi que dise la ligne.** Le contrat refuse
 * d'écrire une telle règle, mais une correction en `psql`, une reprise ou un futur import ne
 * passent pas par lui : la garde vit donc **aussi** ici, du côté qui lit. C'est
 * [M12](../../../../docs/MODELE-DONNEES.md#m12) — la règle métier est portée par le code, pas par
 * une contrainte de base qui recopierait ces quatre codes en SQL et divergerait en silence.
 *
 * `null` quand aucune règle ne vise la cible : rien n'est retiré, c'est le cas courant.
 */
export function hidesReportBlock(rule, block) {
    if (rule === null || LOCKED_REPORT_BLOCKS.includes(block))
        return false;
    return rule.hiddenBlocks.includes(block);
}
/**
 * La règle qui s'applique à un contrôle — à son rapport comme à sa fiche sur la tablette :
 * **celle de son secteur d'abord, celle de son client à défaut** (#661).
 *
 * Le réglage du client est le défaut : un client change rarement ce qu'il attend de ses contrôles.
 * Un secteur ne règle le sien que pour s'en écarter, et une exception qui perdrait contre son
 * défaut ne serait pas une exception. ⚠️ La règle du secteur n'est lue que pour une demande de
 * **son** client : c'est la jointure qui l'écarte d'une vente commandée par un autre (M32).
 *
 * ⚠️ **Une seule des deux, jamais l'union des deux** : l'écran montre **une** règle par cible et un
 * bouton « Tout inclure ». Sous une union, « Tout inclure » sur un secteur livrerait quand même un
 * rapport amputé des blocs de son client — l'aperçu mentirait, et c'est le client qui recevrait
 * le PDF.
 */
export function applicableReportRule(rules) {
    return rules.lot ?? rules.client;
}
/**
 * Les blocs que les **réglages de l'organisation** retirent de tous les rapports, quelle que soit
 * la règle de la cible : l'annexe photographique, qui n'est pas verrouillée (M33). Lue par le rapport et par l'écran des règles — qui, sans elle, annonçait
 * « rapport complet » à une cible dont les photos ne s'impriment pas.
 */
export function blocksUnprintedBySettings(printing) {
    return printing.reportIncludePhotos ? [] : ['photos'];
}
//# sourceMappingURL=report-rule.js.map